import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';

// Shared-rig contract: forbidden. The piece lights itself - one warm key from
// beside the camera, one cold fill, a window on the wall - and the chiaroscuro
// is half the subject. A stage wash would flatten it.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'metronome',
  family: 'object',
  danceability: 0.50,
  energyLevel: 0.40,
  bpmMin: 40,
  bpmMax: 208,
  movement: 0.45,
  tonality: 0.85,
  prefers: { gridStrength: [0.55, 1.0] },
  enabled: true
};

// Metronome: a mechanical metronome, whole, on a table in a dim room. Its
// pendulum beats the tempo - not a picture of it, the thing itself.
//
// THE PHYSICS DOES THE CALIBRATION. A metronome is a compound pendulum: a heavy
// fixed bob under the pivot, a small brass weight that slides on the rod above
// it. Its period depends on nothing but where that weight sits, which is why a
// real one is set by moving the weight to a graduation. So here the weight's
// height is COMPUTED from the tempo by inverting the pendulum law, the scale
// engraved in the slot is drawn from the same law, and the weight therefore
// sits on the right graduation by construction. When the tempo changes with the
// track, the weight slides to its new place in two seconds and the pendulum's
// phase re-locks progressively. That slide is the piece's one gesture.
//
//   T = 2 pi sqrt( I / (M g d) )
//   I = Mb Lb^2 + Ms h^2          the inertia about the pivot
//   M d = Mb Lb - Ms h            the restoring moment arm
//
// THE FRONT IS A SLOT. Not a little window: a vertical opening over nearly the
// whole face, cut 0.15 into the wood, its edges bevelled and lit, its floor
// dark with the scale engraved on it in light. The rod rises from a pivot at
// the bottom of the slot and never leaves it; the weight rides the rod inside
// it. The body is built face by face so the slot is a real hole, not a
// picture of one - a closed frustum with a "slot" painted on it was the first
// version, and the rod lived inside solid wood.

// --- the pendulum ---------------------------------------------------------------
const MB = 1.0, LB = 1.0;          // the fixed bob: mass, distance under the pivot
const MS = 0.9;                    // the slider's mass
const H_MIN = 0.10, H_MAX = 1.06;  // the slider's travel, in rod units
// g such that 208 bpm lands at H_MIN: solved once from the law, see the sweep
// in the acceptance script - 40 bpm then lands at 1.04, inside the travel.
const G = 134.7;
// 0.105 rad, six degrees each side. THE SWING IS BOUNDED BY THE SLOT: a rod
// of 0.96 at 17 degrees sweeps 0.28 either side at its top, through the
// wood. The slot opens a little toward the top (SLOT_W_TOP) and the amplitude
// is what that opening allows - wider, and the front face turned into a door
// with a scale in it. The swing reads on the wall: the rod's shadow, thrown
// obliquely by the key, travels several times the rod's own excursion.
const AMPLITUDE = 0.105;
const LOCK_RATE = 1.6;             // per second, the phase pulled to the beat
const SLIDE_S = 2.0;               // the weight's travel time on a tempo change
const STOP_S = 3.0;                // pause: the swing dies to the centre in this
const RESUME_S = 1.2;
const DEFAULT_BPM = 100;           // no tempo: an honest 100, weight on its mark

// --- the click -------------------------------------------------------------------
const BOUNCE_RAD = 2 * Math.PI / 180;  // the rebound at the end of the travel
const BOUNCE_S = 0.08;
const BOUNCE_HZ = 38;
const SHAKE_S = 0.10;              // the weight's shiver
const SHAKE_PX = 1.0;
const FLASH_S = 0.06;              // the light pulse on the needle
const TOOTH_S = 0.06;              // the escapement's step

// --- the object --------------------------------------------------------------------
const BODY_H = 1.2;
const BASE = 0.55;                 // square footprint
const TOP = 0.40;                  // the truncated top's side (narrower)
// The opening: 22% of the base wide at its foot, and WIDER at its top so the
// rod's arc stays inside the cut - a real metronome's slot is a tall wedge
// for the same reason.
const SLOT_W = 0.22 * BASE;        // the opening's width at the bottom
const SLOT_W_TOP = 0.24;           // and at the top - 58% of the face there, no more
const SLOT_Y0 = 0.08 * BODY_H, SLOT_Y1 = 0.92 * BODY_H;
const SLOT_DEPTH = 0.15;
const BEVEL = 0.012;               // the cut's chamfer, in and back
const PIVOT_Y = SLOT_Y0 + 0.05;    // the rod's pivot, at the bottom of the slot
const ROD_TOP = SLOT_Y1 - 0.03;    // the rod ends under the slot's top
// The rod runs parallel to the sloping front face, so a length along it
// rises by cos(SLOPE) in world y - the scale is drawn with that same cosine.
const SLOPE = Math.atan(((BASE - TOP) / 2) / BODY_H);
const ROD_UNIT = (ROD_TOP - PIVOT_Y - 0.05) / Math.cos(SLOPE) / H_MAX;   // rod units -> length along the rod
const ROD_R = 0.006;
const ROD_PLANE = 0.055;           // the rod's plane, this far behind the front face
const GEAR_TEETH = 24;
const GEAR_R = 0.05;

// --- the room ----------------------------------------------------------------------
const WALL_Z = -0.9;
const TABLE_W = 4.0, TABLE_D = 1.6;   // a finite slab: its front edge is in the frame
const TABLE_FRONT = 0.9;           // the slab runs from the wall to here
const ORBIT_DEG = 8;               // the camera's whole swing, peak to peak
const ORBIT_S = 60;
const PLUNGE_DEG = 8;              // the camera looks down at the object by this
const OBJECT_SHARE = 0.40;         // the body's height on screen, of the frame's
const SINGLE_SHARE = 0.70;         // ?metrosingle
const BASE_NDC = -0.33;            // the base at the lower third
const TABLE_EDGE_NDC = -0.85;      // the slab's front edge, in the bottom 20%
// A STILL LIFE: the object on the table at the LEFT, the picture on the wall
// in the middle, the bare wall and the window's light on the right. The
// object's axis sits at 28% of the frame's width; if a narrow frame would put
// it under the cover's safe zone it moves further left, then shrinks.
const OBJECT_X_NDC = 0.28 * 2 - 1;
const OBJECT_X_NDC_MIN = -0.72;
const SAFE_MARGIN = 0.03;          // height units, between the object and the zone
const WINDOW_DRIFT_S = 140;        // the window's light crossing the wall
const LIGHT_MORNING = [0.62, 0.72, 0.92];  // cold, linear
const LIGHT_EVENING = [1.00, 0.62, 0.34];  // warm
const PALETTE_SHARE = 0.22;        // the phrase's tint, very desaturated

// --- the synthetic grid ---------------------------------------------------------------
const BEATS_PER_PHRASE = 16;

const TIER = {
  0: { shadows: false, mechanism: false, window: false },
  1: { shadows: true, mechanism: false, window: false },
  2: { shadows: true, mechanism: true, window: true }
};
const DEFAULT_TIER = 2;

// The scale: a tick every 4 bpm, a long one every 20, these numbers, and the
// tempo names beside the numbers they belong to.
const SCALE_NUMBERS = [40, 60, 80, 100, 120, 144, 176, 208];
const SCALE_NAMES = { 40: 'Largo', 60: 'Adagio', 80: 'Andante', 120: 'Moderato', 144: 'Allegro', 176: 'Presto' };

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}

// =============================================================================
// THE LAW - pure, exported for the acceptance checks.
// =============================================================================

// The full period, in seconds, of the pendulum with the slider at h.
export function periodForHeight(h) {
  const I = MB * LB * LB + MS * h * h;
  const arm = MB * LB - MS * h;
  return 2 * Math.PI * Math.sqrt(I / (G * Math.max(arm, 1e-6)));
}

// The slider's height for a tempo: a half-period per beat, so T = 120 / bpm.
// T(h) is monotonic on the travel, so a bisection is exact enough.
export function heightForBpm(bpm) {
  const T = 120 / Math.max(1, bpm);
  let lo = H_MIN, hi = H_MAX;
  if (periodForHeight(lo) >= T) return lo;
  if (periodForHeight(hi) <= T) return hi;
  for (let i = 0; i < 48; i++) {
    const mid = (lo + hi) / 2;
    if (periodForHeight(mid) < T) lo = mid; else hi = mid;
  }
  return (lo + hi) / 2;
}

export function bpmForHeight(h) {
  return 120 / periodForHeight(h);
}

// The body's half-width at a height: the frustum tapers from BASE to TOP.
export function halfWidthAt(y) {
  const t = clamp01(y / BODY_H);
  return (BASE + (TOP - BASE) * t) / 2;
}

// The weight's world y for a rod height (what the scale is drawn from too).
export function rodY(h) { return PIVOT_Y + h * ROD_UNIT * Math.cos(SLOPE); }

// The opening's half-width at a height: a wedge, from SLOT_W at the foot to
// SLOT_W_TOP at the top.
export function slotHalfAt(y) {
  const t = clamp01((y - SLOT_Y0) / (SLOT_Y1 - SLOT_Y0));
  return (SLOT_W + (SLOT_W_TOP - SLOT_W) * t) / 2;
}

// =============================================================================
// GEOMETRY - the body face by face, so the slot is a hole.
// =============================================================================

// A quad builder: four corners, counter-clockwise seen from outside.
class QuadMesh {
  constructor() { this.pos = []; this.nrm = []; this.uv = []; }
  quad(a, b, c, d, uvs) {
    const ab = [b[0] - a[0], b[1] - a[1], b[2] - a[2]];
    const ac = [c[0] - a[0], c[1] - a[1], c[2] - a[2]];
    const n = [ab[1] * ac[2] - ab[2] * ac[1], ab[2] * ac[0] - ab[0] * ac[2], ab[0] * ac[1] - ab[1] * ac[0]];
    const l = Math.hypot(n[0], n[1], n[2]) || 1;
    n[0] /= l; n[1] /= l; n[2] /= l;
    const u = uvs || [[0, 0], [1, 0], [1, 1], [0, 1]];
    for (const [p, t] of [[a, u[0]], [b, u[1]], [c, u[2]], [a, u[0]], [c, u[2]], [d, u[3]]]) {
      this.pos.push(p[0], p[1], p[2]); this.nrm.push(n[0], n[1], n[2]); this.uv.push(t[0], t[1]);
    }
  }
  build() {
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(this.pos, 3));
    g.setAttribute('normal', new THREE.Float32BufferAttribute(this.nrm, 3));
    g.setAttribute('uv', new THREE.Float32BufferAttribute(this.uv, 2));
    return g;
  }
}

// The wood: the frustum's back, sides, top, bottom, and the front face with the
// slot cut out - two side pieces, a bottom and a top strip - plus the bevels.
function buildBody() {
  const q = new QuadMesh();
  const hw0 = halfWidthAt(0), hw1 = halfWidthAt(BODY_H);
  const H = BODY_H;
  const B = (x, z) => [x * hw0, 0, z * hw0];
  const T = (x, z) => [x * hw1, H, z * hw1];
  q.quad(B(1, -1), B(-1, -1), T(-1, -1), T(1, -1));         // back
  q.quad(B(-1, -1), B(-1, 1), T(-1, 1), T(-1, -1));         // left
  q.quad(B(1, 1), B(1, -1), T(1, -1), T(1, 1));             // right
  q.quad(T(-1, 1), T(1, 1), T(1, -1), T(-1, -1));           // top
  q.quad(B(-1, -1), B(1, -1), B(1, 1), B(-1, 1));           // bottom
  // the front face around the slot. A point on the front at height y sits at
  // z = halfWidthAt(y); the face is planar, so quads on it are planar too.
  const F = (x, y) => [x, y, halfWidthAt(y)];
  const s0 = slotHalfAt(SLOT_Y0), s1 = slotHalfAt(SLOT_Y1);
  const lerpW = (y) => halfWidthAt(y);
  q.quad(F(-hw0, 0), F(hw0, 0), F(lerpW(SLOT_Y0), SLOT_Y0), F(-lerpW(SLOT_Y0), SLOT_Y0));      // bottom strip
  q.quad(F(-lerpW(SLOT_Y1), SLOT_Y1), F(lerpW(SLOT_Y1), SLOT_Y1), F(hw1, H), F(-hw1, H));      // top strip
  q.quad(F(-lerpW(SLOT_Y0), SLOT_Y0), F(-s0, SLOT_Y0), F(-s1, SLOT_Y1), F(-lerpW(SLOT_Y1), SLOT_Y1));   // left
  q.quad(F(s0, SLOT_Y0), F(lerpW(SLOT_Y0), SLOT_Y0), F(lerpW(SLOT_Y1), SLOT_Y1), F(s1, SLOT_Y1));       // right
  // the bevels: a chamfer from the face's edge inward and back
  const Fi = (x, y) => [x, y, halfWidthAt(y) - BEVEL];
  const b = BEVEL;
  q.quad(F(-s0, SLOT_Y0), Fi(-s0 + b, SLOT_Y0 + b), Fi(-s1 + b, SLOT_Y1 - b), F(-s1, SLOT_Y1));
  q.quad(Fi(s0 - b, SLOT_Y0 + b), F(s0, SLOT_Y0), F(s1, SLOT_Y1), Fi(s1 - b, SLOT_Y1 - b));
  q.quad(F(-s0, SLOT_Y0), F(s0, SLOT_Y0), Fi(s0 - b, SLOT_Y0 + b), Fi(-s0 + b, SLOT_Y0 + b));
  q.quad(Fi(-s1 + b, SLOT_Y1 - b), Fi(s1 - b, SLOT_Y1 - b), F(s1, SLOT_Y1), F(-s1, SLOT_Y1));
  return q.build();
}

// The cavity: its four walls from the chamfer down to the floor, and the floor
// itself (a separate mesh, it carries the scale).
function buildCavityWalls() {
  const q = new QuadMesh();
  const y0 = SLOT_Y0 + BEVEL, y1 = SLOT_Y1 - BEVEL;
  const s0 = slotHalfAt(y0) - BEVEL, s1 = slotHalfAt(y1) - BEVEL;
  const Fi = (x, y) => [x, y, halfWidthAt(y) - BEVEL];
  const D = (x, y) => [x, y, halfWidthAt(y) - SLOT_DEPTH];
  q.quad(Fi(-s0, y0), Fi(-s1, y1), D(-s1, y1), D(-s0, y0));   // left wall (faces +x)
  q.quad(Fi(s1, y1), Fi(s0, y0), D(s0, y0), D(s1, y1));       // right wall (faces -x)
  q.quad(Fi(s0, y0), Fi(-s0, y0), D(-s0, y0), D(s0, y0));     // bottom wall (faces +y)
  q.quad(Fi(-s1, y1), Fi(s1, y1), D(s1, y1), D(-s1, y1));     // top wall (faces -y)
  return q.build();
}

// The floor is a rectangle as wide as the slot's top, so the scale's canvas
// maps onto it squarely; the wedge's walls hide its sides lower down.
function buildCavityFloor() {
  const q = new QuadMesh();
  const y0 = SLOT_Y0 + BEVEL, y1 = SLOT_Y1 - BEVEL;
  const s1 = slotHalfAt(y1) - BEVEL;
  const D = (x, y) => [x, y, halfWidthAt(y) - SLOT_DEPTH];
  q.quad(D(-s1, y0), D(s1, y0), D(s1, y1), D(-s1, y1), [[0, 0], [1, 0], [1, 1], [0, 1]]);
  return q.build();
}

// =============================================================================

export class MetronomeVisualizer extends Visualizer {
  constructor(options) {
    super();
    const opts = options || {};
    let tier = opts.qualityTier != null ? opts.qualityTier : DEFAULT_TIER;
    if (!TIER[tier]) tier = 0;
    this._tier = tier;
    this._layout = TIER[tier];

    this._time = 0;
    this._paused = false;
    this._bpm = DEFAULT_BPM;       // the tempo the weight is travelling TO
    this._h = heightForBpm(DEFAULT_BPM);
    this._hFrom = this._h; this._hTo = this._h; this._slideT = 1;
    this._phi = 0;                 // the pendulum's phase: angle = A cos(phi)
    this._lastHalf = null;         // the last half-period boundary seen
    this._lastPhraseIndex = null;
    this._amp = 1;                 // 0 stopped, 1 swinging
    this._clickAt = -1e9;
    this._clickSide = 1;
    this._toothAt = -1e9;
    this._gearBase = 0;            // the escapement's settled angle, in radians
    this._lowBand = 0.5;
    this._lastPalette = null;
    this._tint = new THREE.Color(1, 1, 1);
    this._tintTarget = new THREE.Color(1, 1, 1);
    this._progress = 0;
    this._phraseCount = 0;
    this._lastAspect = 0;

    // ?metrolayer=object|table|wall|light shows one layer alone ('light' is
    // a clay render: every surface matte white, so the lighting reads bare);
    // ?metroambient=1 adds a white ambient at 0.5; ?metrosingle=1 is the
    // object alone, close, front on, still.
    const layer = urlParam('metrolayer');
    this._debugLayer = ['object', 'table', 'wall', 'light'].indexOf(layer) >= 0 ? layer : null;
    this._debugAmbient = urlParam('metroambient') === '1';
    this._single = urlParam('metrosingle') === '1';
    const fb = parseFloat(urlParam('metrobpm'));
    this._forcedBpm = Number.isFinite(fb) && fb > 0 ? fb : null;
    this._forceSlide = urlParam('metroslide') === '1';
  }

  // --- building ------------------------------------------------------------------

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    const L = this._layout;
    const self = this;

    // A PRIVATE SCENE, RENDERED WITH MSAA. The shared pipeline has no
    // anti-aliasing at all (antialias: false, a plain composer target) and
    // renders under 1:1, which every additive visualizer hides and a lit
    // solid with hard silhouettes cannot: the body's edges crawled with the
    // camera's orbit. So the room is rendered into a 4x multisampled target
    // of the drawing buffer's size and shown through one full-frame quad in
    // the shared scene. The quad's onBeforeRender does the nested render
    // (the same mechanism Reflector uses).
    this._inner = new THREE.Scene();
    this._root = new THREE.Group();
    this._inner.add(this._root);

    // --- materials. ColorManagement is off in this build: these hex values
    // reach the shader as LINEAR, so they are darker than they read.
    this._woodTex = this._makeWoodTexture();
    this._matWood = new THREE.MeshStandardMaterial({ color: 0x8a5a34, map: this._woodTex, roughness: 0.38, metalness: 0.0 });
    this._matCavity = new THREE.MeshStandardMaterial({ color: 0x1e1610, roughness: 0.85, metalness: 0.0 });
    this._matBrass = new THREE.MeshStandardMaterial({ color: 0xc8963a, roughness: 0.28, metalness: 0.9 });
    this._matSteel = new THREE.MeshStandardMaterial({ color: 0xb8bcc2, roughness: 0.25, metalness: 0.95, emissive: 0x000000 });
    this._matTable = new THREE.MeshStandardMaterial({ color: 0x3a2414, map: this._woodTex, roughness: 0.6, metalness: 0.0 });
    this._matTableEdge = new THREE.MeshStandardMaterial({ color: 0x2a1a0e, roughness: 0.7, metalness: 0.0 });
    this._matWall = new THREE.MeshStandardMaterial({ color: 0x4a4540, roughness: 0.95, metalness: 0.0 });
    this._matFrame = new THREE.MeshStandardMaterial({ color: 0x1c1410, roughness: 0.5, metalness: 0.1 });
    this._plateMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.75, metalness: 0.1 });
    this._mats = [this._matWood, this._matCavity, this._matBrass, this._matSteel, this._matTable, this._matTableEdge, this._matWall, this._matFrame, this._plateMat];
    this._geos = [];
    const geo = (g) => { this._geos.push(g); return g; };
    const mesh = (g, m, cast, recv) => {
      const o = new THREE.Mesh(g, m);
      o.castShadow = !!cast && L.shadows;
      o.receiveShadow = !!recv && L.shadows;
      return o;
    };

    // --- the body, with its slot cut in
    this._object = new THREE.Group();
    this._root.add(this._object);
    const body = mesh(geo(buildBody()), this._matWood, true, true);
    this._body = body;
    this._object.add(body);
    const walls = mesh(geo(buildCavityWalls()), this._matCavity, false, true);
    this._object.add(walls);
    this._buildScaleTexture();
    const floor = mesh(geo(buildCavityFloor()), this._plateMat, false, true);
    this._object.add(floor);
    this._floor = floor;

    // --- the pendulum: a frame tilted with the front face (the rod runs
    // parallel to it, ROD_PLANE behind), and inside it the swing about z at
    // the pivot. The pivot is at the bottom of the slot and never moves.
    this._pendulumFrame = new THREE.Group();
    this._pendulumFrame.position.set(0, PIVOT_Y, halfWidthAt(PIVOT_Y) - ROD_PLANE);
    this._pendulumFrame.rotation.x = -SLOPE;
    this._object.add(this._pendulumFrame);
    this._pendulum = new THREE.Group();
    this._pendulumFrame.add(this._pendulum);
    const rodLen = ROD_TOP - PIVOT_Y;
    const rod = mesh(geo(new THREE.CylinderGeometry(ROD_R, ROD_R, rodLen + 0.06, 10)), this._matSteel, true, false);
    rod.position.y = (rodLen - 0.06) / 2;
    this._rod = rod;
    this._pendulum.add(rod);
    const finial = mesh(geo(new THREE.SphereGeometry(0.014, 12, 10)), this._matBrass, true, false);
    finial.position.y = rodLen;
    this._pendulum.add(finial);
    const boss = mesh(geo(new THREE.CylinderGeometry(0.018, 0.018, 0.02, 14)), this._matBrass, false, false);
    boss.rotation.x = Math.PI / 2;
    this._pendulum.add(boss);
    // the counterweight, under the pivot, behind the face: the inertia
    const counter = mesh(geo(new THREE.CylinderGeometry(0.05, 0.05, 0.025, 20)), this._matBrass, false, false);
    counter.rotation.x = Math.PI / 2;
    counter.position.y = -0.08;
    this._pendulum.add(counter);
    // the slider: a brass trapezoid gripping the rod, its clamp screw in front
    this._slider = new THREE.Group();
    const grip = mesh(geo(new THREE.CylinderGeometry(0.028, 0.038, 0.055, 4)), this._matBrass, true, false);
    grip.rotation.y = Math.PI / 4;
    grip.scale.z = 0.45;
    this._slider.add(grip);
    const screw = mesh(geo(new THREE.CylinderGeometry(0.007, 0.007, 0.03, 8)), this._matSteel, false, false);
    screw.rotation.x = Math.PI / 2;
    screw.position.z = 0.02;
    this._slider.add(screw);
    this._pendulum.add(this._slider);

    // --- the key on the right flank, the stop lever on the left
    const keyY = 0.36;
    const keyX = halfWidthAt(keyY) + 0.03;
    const keyStem = mesh(geo(new THREE.CylinderGeometry(0.011, 0.011, 0.075, 10)), this._matBrass, true, false);
    keyStem.rotation.z = Math.PI / 2;
    keyStem.position.set(keyX, keyY, 0.02);
    this._object.add(keyStem);
    const keyBow = mesh(geo(new THREE.BoxGeometry(0.018, 0.085, 0.028)), this._matBrass, true, false);
    keyBow.position.set(keyX + 0.045, keyY, 0.02);
    this._object.add(keyBow);
    const leverY = 0.55;
    const lever = mesh(geo(new THREE.BoxGeometry(0.05, 0.012, 0.012)), this._matSteel, true, false);
    lever.position.set(-halfWidthAt(leverY) - 0.02, leverY, 0.03);
    lever.rotation.z = 0.35;
    this._object.add(lever);
    if (L.mechanism) {
      // the escapement wheel, on the face under the slot
      const gear = mesh(geo(this._gearGeometry(GEAR_R, GEAR_TEETH)), this._matBrass, false, false);
      const gy = SLOT_Y0 * 0.5;
      gear.position.set(0, gy, halfWidthAt(gy) - 0.006);
      this._gear = gear;
      this._object.add(gear);
    }

    // --- the room: a finite table slab with its front edge, the wall, the
    // empty frame where the cover hangs
    const slab = new THREE.Group();
    const top = mesh(geo(new THREE.PlaneGeometry(TABLE_W, TABLE_D)), this._matTable, false, true);
    top.rotation.x = -Math.PI / 2;
    top.position.set(0, 0, TABLE_FRONT - TABLE_D / 2);
    slab.add(top);
    const edge = mesh(geo(new THREE.BoxGeometry(TABLE_W, 0.05, 0.05)), this._matTableEdge, true, false);
    edge.position.set(0, -0.025, TABLE_FRONT - 0.025);
    slab.add(edge);
    this._tableTop = top; this._tableEdge = edge;
    this._table = slab;
    this._root.add(slab);
    const wall = mesh(geo(new THREE.PlaneGeometry(8, 8)), this._matWall, false, true);
    wall.position.set(0, 3.9, WALL_Z);
    this._root.add(wall);
    this._wall = wall;
    this._frame = new THREE.Group();
    this._frameBars = [];
    for (let i = 0; i < 4; i++) {
      const bar = mesh(geo(new THREE.BoxGeometry(1, 1, 0.03)), this._matFrame, true, false);
      this._frameBars.push(bar);
      this._frame.add(bar);
    }
    this._frame.position.z = WALL_Z + 0.02;
    this._root.add(this._frame);

    // --- the window's light on the wall: a soft rectangle with a mullion,
    // additive, sliding slowly. A quad on the wall is what a window's patch
    // of light actually is.
    if (L.window) {
      this._windowUniforms = { uColor: { value: new THREE.Color(1, 1, 1) }, uGain: { value: 0.16 } };
      this._windowMat = new THREE.ShaderMaterial({
        uniforms: this._windowUniforms,
        vertexShader: `
          precision mediump float;
          varying vec2 vUv;
          void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
        fragmentShader: `
          precision mediump float;
          uniform vec3 uColor;
          uniform float uGain;
          varying vec2 vUv;
          void main() {
            vec2 p = abs(vUv - 0.5) * 2.0;
            float edge = (1.0 - smoothstep(0.80, 1.0, p.x)) * (1.0 - smoothstep(0.80, 1.0, p.y));
            float mull = (1.0 - smoothstep(0.03, 0.07, abs(vUv.x - 0.5))) + (1.0 - smoothstep(0.03, 0.07, abs(vUv.y - 0.5)));
            float a = edge * (1.0 - 0.85 * clamp(mull, 0.0, 1.0));
            gl_FragColor = vec4(uColor * uGain * a, 1.0);
          }`,
        transparent: true, blending: THREE.AdditiveBlending, depthWrite: false
      });
      this._window = new THREE.Mesh(geo(new THREE.PlaneGeometry(1.7, 2.1)), this._windowMat);
      this._window.position.set(1.4, 2.4, WALL_Z + 0.01);
      this._window.rotation.z = -0.10;
      this._root.add(this._window);
    }

    // --- the light. THE KEY IS BESIDE THE CAMERA, 30 degrees to its left
    // and a little above, so it lights the FRONT face and the left flank -
    // that is where everything happens. A lamp off to the side lit the
    // right flank and left the face, the slot and the scale in shadow. Its
    // position is written by _placeCamera, with the camera it follows.
    // The key: ON THE METRONOME, from the left and a little above, the cover
    // in its falloff. It is the ONE light that casts shadows: the silhouette
    // of the object, pendulum included, thrown obliquely onto the wall - and
    // the rod's shadow swinging there is the best thing in the scene.
    this._key = new THREE.SpotLight(0xffd9a8, 21.0, 14, 0.62, 0.55, 1.2);
    this._key.target.position.set(0, BODY_H * 0.5, 0);
    this._inner.add(this._key.target);
    if (L.shadows) {
      // 2048 on the big tiers, 1024 on the Pi's - and a frustum that covers
      // only the object and the wall behind it, which is where the effective
      // resolution comes from. bias/normalBias against the bands that walked
      // across the wall as the pendulum moved (acne), radius 4 for a lamp's
      // soft penumbra.
      this._key.castShadow = true;
      const mapSize = this._tier >= 2 ? 2048 : 1024;
      this._key.shadow.mapSize.set(mapSize, mapSize);
      this._key.shadow.bias = -0.0005;
      this._key.shadow.normalBias = 0.02;
      this._key.shadow.radius = 4;
      this._key.shadow.camera.near = 0.6;
      this._key.shadow.camera.far = 6.0;
    }
    this._fill = new THREE.DirectionalLight(0x8fa8d8, 0.45);
    this._fill.position.set(2.5, 2.0, 1.5);
    this._fill.castShadow = false;
    this._ambient = new THREE.AmbientLight(0x6a6258, 0.55);
    this._inner.add(this._key, this._fill, this._ambient);
    this._lights = [this._key, this._key.target, this._fill, this._ambient];

    // Shadow maps are a renderer-wide setting; onBeforeRender is the one place
    // a visualizer reaches the renderer, and the previous value is restored
    // on dispose (the contract silk.js / origami.js established). The switch
    // leaves every program compiled in that frame wrong, so the materials are
    // recompiled on the second and fourth RENDERS after it - measured: a
    // recompile in the same frame did not take, one a frame later did.
    this._renderedFrames = 0;
    this._bufferH = 720;
    // Half float on the big tier (the highlights keep their headroom for the
    // bloom); bytes on the Pi's tiers, where a multisampled float target is
    // not a bet worth taking. Nearest filtering: the blit is 1:1, and V3D
    // cannot filter float textures anyway.
    const rtType = this._tier >= 2 ? THREE.HalfFloatType : THREE.UnsignedByteType;
    this._rt = new THREE.WebGLRenderTarget(4, 4, {
      samples: 4, type: rtType, depthBuffer: true, stencilBuffer: false,
      minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter, generateMipmaps: false
    });
    this._quadMat = new THREE.ShaderMaterial({
      uniforms: { tScene: { value: this._rt.texture } },
      vertexShader: `
        precision mediump float;
        varying vec2 vUv;
        void main() { vUv = uv; gl_Position = vec4(position.xy, 0.9999, 1.0); }`,
      fragmentShader: `
        precision mediump float;
        uniform sampler2D tScene;
        varying vec2 vUv;
        void main() { gl_FragColor = vec4(texture2D(tScene, vUv).rgb, 1.0); }`,
      depthTest: false, depthWrite: false
    });
    this._quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this._quadMat);
    this._quad.frustumCulled = false;
    this._quad.renderOrder = -3000;
    scene.add(this._quad);
    this._quad.onBeforeRender = function (renderer, outerScene, camera) {
      renderer.getDrawingBufferSize(_size);
      self._bufferH = _size.y;
      if (self._rt.width !== _size.x || self._rt.height !== _size.y) self._rt.setSize(_size.x, _size.y);
      if (!self._rendererRef) {
        self._rendererRef = renderer;
        self._shadowWasEnabled = renderer.shadowMap.enabled;
        if (L.shadows) {
          renderer.shadowMap.enabled = true;
          renderer.shadowMap.type = THREE.PCFSoftShadowMap;
        }
      }
      self._renderedFrames++;
      if (self._renderedFrames === 2 || self._renderedFrames === 4) {
        for (const m of self._mats) m.needsUpdate = true;
      }
      // the nested render: the room into the multisampled target, with the
      // same camera the outer pass uses
      const prevTarget = renderer.getRenderTarget();
      const prevXr = renderer.xr.enabled;
      renderer.xr.enabled = false;
      renderer.setRenderTarget(self._rt);
      renderer.clear(true, true, false);
      renderer.render(self._inner, camera);
      renderer.setRenderTarget(prevTarget);
      renderer.xr.enabled = prevXr;
    };

    // --- debug: one layer alone, a forced ambient, the single close-up
    if (this._debugAmbient) {
      this._debugAmbientLight = new THREE.AmbientLight(0xffffff, 0.5);
      this._inner.add(this._debugAmbientLight);
      this._lights.push(this._debugAmbientLight);
    }
    if (this._debugLayer) this._applyDebugLayer();
    if (this._single) {
      this._table.visible = false; this._wall.visible = false; this._frame.visible = false;
      if (this._window) this._window.visible = false;
    }

    this._placeCamera(0);
    this._placeFrame();
    this._applyPendulum();
    this._logInventory();
  }

  // A toothed wheel, as a flat extrusion.
  _gearGeometry(r, teeth) {
    const shape = new THREE.Shape();
    const n = teeth * 2;
    for (let i = 0; i <= n; i++) {
      const a = i / n * Math.PI * 2;
      const rr = (i % 2 === 0) ? r : r * 0.82;
      const x = Math.cos(a) * rr, y = Math.sin(a) * rr;
      if (i === 0) shape.moveTo(x, y); else shape.lineTo(x, y);
    }
    const hole = new THREE.Path();
    hole.absarc(0, 0, r * 0.25, 0, Math.PI * 2, true);
    shape.holes.push(hole);
    return new THREE.ExtrudeGeometry(shape, { depth: 0.01, bevelEnabled: false });
  }

  // Wood grain, procedural: long streaks of varying darkness with fine pores,
  // on a canvas, tiled. Without a document (the acceptance script) the wood
  // is plain.
  _makeWoodTexture() {
    if (typeof document === 'undefined') return null;
    const W = 512, H = 512;
    const cv = document.createElement('canvas');
    cv.width = W; cv.height = H;
    const g = cv.getContext('2d');
    g.fillStyle = '#9a6a3e';
    g.fillRect(0, 0, W, H);
    for (let x = 0; x < W; x++) {
      const ring = 0.5 + 0.5 * Math.sin(x * 0.11 + Math.sin(x * 0.023) * 3.0);
      const dark = 0.10 + 0.22 * Math.pow(ring, 3);
      g.fillStyle = 'rgba(30,14,6,' + dark.toFixed(3) + ')';
      g.fillRect(x, 0, 1, H);
    }
    for (let i = 0; i < 4000; i++) {
      const x = Math.random() * W, y = Math.random() * H;
      g.fillStyle = 'rgba(20,10,4,' + (0.08 + 0.15 * Math.random()).toFixed(3) + ')';
      g.fillRect(x, y, 1, 4 + Math.random() * 14);
    }
    const tex = new THREE.CanvasTexture(cv);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
    tex.repeat.set(1.5, 1.5);
    tex.anisotropy = 4;
    return tex;
  }

  // The scale on the slot's floor: light on dark, drawn ONCE on a 256 x 1024
  // canvas from the same law that places the weight. A tick every 4 bpm, a
  // long one every 20, the numbers, and the tempo names beside theirs.
  _buildScaleTexture() {
    if (typeof document === 'undefined') return;
    const W = 256, H = 1024;
    const cv = document.createElement('canvas');
    cv.width = W; cv.height = H;
    const g = cv.getContext('2d');
    g.fillStyle = '#1e1a14';
    g.fillRect(0, 0, W, H);
    const y0 = SLOT_Y0 + BEVEL, y1 = SLOT_Y1 - BEVEL;
    const vOf = (worldY) => H - (worldY - y0) / (y1 - y0) * H;
    const ink = '#e8d9b0';
    g.strokeStyle = ink; g.fillStyle = ink;
    g.textBaseline = 'middle';
    // the ticks centred, the numbers left of them, the names right of them
    for (let bpm = 40; bpm <= 208; bpm += 4) {
      const y = vOf(rodY(heightForBpm(bpm)));
      const long = bpm % 20 === 0 || bpm === 208;
      g.lineWidth = long ? 3 : 1.5;
      const half = W * (long ? 0.11 : 0.06);
      g.beginPath(); g.moveTo(W * 0.5 - half, y); g.lineTo(W * 0.5 + half, y); g.stroke();
    }
    g.font = 'bold 26px Georgia, serif';
    g.textAlign = 'right';
    for (const bpm of SCALE_NUMBERS) {
      const y = vOf(rodY(heightForBpm(bpm)));
      g.fillText(String(bpm), W * 0.36, y);
      const name = SCALE_NAMES[bpm];
      if (name) {
        g.save();
        g.font = 'italic 14px Georgia, serif';
        g.textAlign = 'left';
        g.fillText(name, W * 0.64, y);
        g.restore();
      }
    }
    const tex = new THREE.CanvasTexture(cv);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.anisotropy = 8;
    tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;
    this._plateTex = tex;
    this._plateMat.map = tex;
    this._plateMat.needsUpdate = true;
  }

  // --- placement ---------------------------------------------------------------

  // The framing: the whole object at OBJECT_SHARE of the frame's height, seen
  // from PLUNGE_DEG above, its base at the lower third - or lower, when the
  // cover would otherwise sit on it. The distance comes from the fov; the
  // aim height is solved by iteration on the real projection, so the tilt
  // is accounted for exactly. Redone whenever the aspect changes.
  _solveFraming() {
    const cam = this._camera;
    // R19: the object keeps clear of the stage block, not of the cover
    const c = safeZoneUniforms.uStageCenter.value, h = safeZoneUniforms.uStageHalfSize.value;
    const feather = safeZoneUniforms.uSafeFeather.value;
    const share = (this._single ? SINGLE_SHARE : OBJECT_SHARE) * (this._shareScale || 1);
    // the fov gives the first distance; the front face is nearer than the
    // axis and the camera tilts, so the on-screen height is measured on the
    // real projection and the distance corrected until it is the share asked
    let dist = (BODY_H / share) / 2 / Math.tan(cam.fov * Math.PI / 360);
    const plunge = this._single ? 0 : PLUNGE_DEG * Math.PI / 180;
    // THE LOWER THIRD, AS ASKED. With a cover whose bottom edge is above
    // that, the object's top runs behind the cover: at 40% of the frame and
    // a base at -0.33 the top is at +0.47, and no cover on this page ends
    // above it. The numbers are the brief's; the overlap is theirs too.
    const baseNdc = this._single ? -share : BASE_NDC;
    let aimY = 0.6;
    const tmp = new THREE.Vector3(), tmp2 = new THREE.Vector3();
    const probeCam = new THREE.PerspectiveCamera(cam.fov, cam.aspect, cam.near, cam.far);
    const place = () => {
      probeCam.position.set(0, aimY + dist * Math.sin(plunge), dist * Math.cos(plunge));
      probeCam.lookAt(0, aimY, 0);
      probeCam.updateMatrixWorld();
      probeCam.updateProjectionMatrix();
    };
    for (let outer = 0; outer < 8; outer++) {
      for (let i = 0; i < 12; i++) {
        place();
        tmp.set(0, 0, halfWidthAt(0)).project(probeCam);
        const err = tmp.y - baseNdc;
        if (Math.abs(err) < 1e-4) break;
        aimY += err * (BODY_H / share) / 2;
      }
      place();
      tmp.set(0, 0, halfWidthAt(0)).project(probeCam);
      tmp2.set(0, BODY_H, halfWidthAt(BODY_H)).project(probeCam);
      const seen = (tmp2.y - tmp.y) / 2;
      if (Math.abs(seen - share) < 0.002) break;
      dist *= seen / share;
    }
    this._dist = dist; this._aimY = aimY; this._plunge = plunge;
    // THE OBJECT AT THE LEFT: its axis at OBJECT_X_NDC, solved on the real
    // projection, then pushed further left while its right edge would run
    // into the cover's safe zone, and shrunk when even the far left does.
    if (!this._single) {
      place();
      const aspect = probeCam.aspect;
      const zoneLeft = (c.x - h.x - feather * 0.5 - SAFE_MARGIN) / aspect;   // ndc
      let x = 0, wantX = OBJECT_X_NDC;
      for (let i = 0; i < 12; i++) {
        tmp.set(x, BODY_H * 0.5, 0).project(probeCam);
        const err = tmp.x - wantX;
        if (Math.abs(err) < 1e-4) break;
        x -= err * this._halfHAtObjectFor(dist) * aspect;
      }
      // the object's right edge on screen, over its height
      let right = -1;
      for (let k = 0; k <= 4; k++) {
        const y = BODY_H * k / 4;
        tmp.set(x + halfWidthAt(y), y, halfWidthAt(y)).project(probeCam);
        right = Math.max(right, tmp.x);
      }
      if (right > zoneLeft) {
        const shift = (right - zoneLeft) * this._halfHAtObjectFor(dist) * aspect;
        x -= shift;
        tmp.set(x, BODY_H * 0.5, 0).project(probeCam);
        if (tmp.x < OBJECT_X_NDC_MIN) {
          // no room: a smaller object, solved again
          this._shareScale = (this._shareScale || 1) * 0.9;
          if (this._shareScale > 0.5) { this._solveFraming(); return; }
        }
      }
      this._objectX = x;
    } else {
      this._objectX = 0;
    }
    this._object.position.x = this._objectX;
    this._key.target.position.set(this._objectX, BODY_H * 0.5, 0);
    // the table's front edge, where it lands in the bottom 20% of the frame
    if (!this._single && this._table) {
      probeCam.position.set(0, aimY + dist * Math.sin(plunge), dist * Math.cos(plunge));
      probeCam.lookAt(0, aimY, 0);
      probeCam.updateMatrixWorld();
      let lo = 0.2, hi = dist * Math.cos(plunge) - 0.2;
      for (let i = 0; i < 24; i++) {
        const mid = (lo + hi) / 2;
        tmp.set(0, 0, mid).project(probeCam);
        if (tmp.y > TABLE_EDGE_NDC) lo = mid; else hi = mid;
      }
      const front = (lo + hi) / 2;
      const depth = front - WALL_Z;
      this._tableTop.scale.set(1, depth / TABLE_D, 1);
      this._tableTop.position.set(0, 0, WALL_Z + depth / 2);
      this._tableEdge.position.set(0, -0.025, front - 0.025);
    }
  }

  _halfHAtObjectFor(dist) { return dist * Math.tan(this._camera.fov * Math.PI / 360); }

  _placeCamera(t) {
    const cam = this._camera;
    const aspect = safeZoneUniforms.uAspect.value || cam.aspect;
    if (aspect !== this._lastAspect || this._dist == null) { this._lastAspect = aspect; this._shareScale = 1; this._solveFraming(); }
    const dist = this._dist, aimY = this._aimY, plunge = this._plunge;
    const orbit = this._single ? 0 : Math.sin(t * Math.PI * 2 / ORBIT_S) * (ORBIT_DEG / 2) * Math.PI / 180;
    cam.position.set(Math.sin(orbit) * dist * Math.cos(plunge), aimY + dist * Math.sin(plunge), Math.cos(orbit) * dist * Math.cos(plunge));
    cam.lookAt(0, aimY, 0);
    // the key: left of the object and above it, in front of the table's
    // edge, aimed at the object; its throw crosses the object to the wall
    // behind, where the cover sits in its falloff
    // FIXED IN THE ROOM, not following the orbit: a light that moves with
    // the camera re-projects its shadow map every frame and the shadow's
    // edge swims on the wall.
    const ox = this._objectX || 0;
    this._key.position.set(ox - 1.15, BODY_H + 0.95, 2.3);
    this._halfHAtObject = dist * Math.tan(cam.fov * Math.PI / 360);
  }

  // The empty frame drawn where the cover is: the overlay rectangle's corners,
  // cast from the camera onto the wall.
  _placeFrame() {
    const c = safeZoneUniforms.uSafeCenter.value, h = safeZoneUniforms.uSafeHalfSize.value;
    const aspect = safeZoneUniforms.uAspect.value || this._camera.aspect;
    const cam = this._camera;
    cam.updateMatrixWorld();
    const onWall = (hx, hy) => {
      const ndc = new THREE.Vector3(hx / aspect, hy, 0.5).unproject(cam);
      const dir = ndc.sub(cam.position).normalize();
      const t = (WALL_Z + 0.02 - cam.position.z) / dir.z;
      return cam.position.clone().addScaledVector(dir, t);
    };
    const tl = onWall(c.x - h.x, c.y + h.y), br = onWall(c.x + h.x, c.y - h.y);
    const cx = (tl.x + br.x) / 2, cy = (tl.y + br.y) / 2;
    const hw = Math.abs(br.x - tl.x) / 2 + 0.05, hh = Math.abs(tl.y - br.y) / 2 + 0.05;
    const bars = this._frameBars, t = 0.06;
    bars[0].position.set(cx, cy + hh, 0); bars[0].scale.set(hw * 2 + t, t, 1);
    bars[1].position.set(cx, cy - hh, 0); bars[1].scale.set(hw * 2 + t, t, 1);
    bars[2].position.set(cx - hw, cy, 0); bars[2].scale.set(t, hh * 2, 1);
    bars[3].position.set(cx + hw, cy, 0); bars[3].scale.set(t, hh * 2, 1);
  }

  // --- the pendulum ---------------------------------------------------------------

  _applyPendulum() {
    const t = this._time;
    let angle = this._single ? 0 : AMPLITUDE * this._amp * Math.cos(this._phi);
    const since = t - this._clickAt;
    if (!this._single && since >= 0 && since < BOUNCE_S * 3) {
      const gain = 0.6 + 0.8 * this._lowBand;
      angle += this._clickSide * BOUNCE_RAD * gain * Math.exp(-since / BOUNCE_S) * Math.cos(since * BOUNCE_HZ * Math.PI * 2);
    }
    this._pendulum.rotation.z = angle;
    let y = this._h * ROD_UNIT;
    if (since >= 0 && since < SHAKE_S) {
      const px = 2 * (this._halfHAtObject || 1) / Math.max(1, this._bufferH || 720);
      y += Math.sin(since * 220) * SHAKE_PX * px * (1 - since / SHAKE_S);
    }
    this._slider.position.y = y;
    const flash = since >= 0 && since < FLASH_S ? (1 - since / FLASH_S) : 0;
    this._matSteel.emissive.setRGB(0.55 * flash, 0.5 * flash, 0.4 * flash);
    if (this._gear) {
      const ts = t - this._toothAt;
      const step = Math.PI * 2 / GEAR_TEETH;
      const partial = ts >= 0 && ts < TOOTH_S ? (ts / TOOTH_S) : 1;
      this._gear.rotation.z = -(this._gearBase - step * (1 - partial));
    }
  }

  // The click: at each end of the travel. Called when the phase crosses a
  // multiple of pi - which is exactly a beat when the pendulum is locked.
  _click(side) {
    this._clickAt = this._time;
    this._clickSide = side;
    if (this._gear) {
      this._gearBase += Math.PI * 2 / GEAR_TEETH;
      this._toothAt = this._time;
    }
  }

  // --- debug ------------------------------------------------------------------------

  // ?metrolayer: hide everything but one layer. 'light' keeps every surface
  // and paints it matte white - a clay render of the lighting alone.
  _applyDebugLayer() {
    const L = this._debugLayer;
    if (L === 'object') { this._table.visible = false; this._wall.visible = false; this._frame.visible = false; if (this._window) this._window.visible = false; }
    if (L === 'table') { this._object.visible = false; this._wall.visible = false; this._frame.visible = false; if (this._window) this._window.visible = false; }
    if (L === 'wall') { this._object.visible = false; this._table.visible = false; }
    if (L === 'light') {
      this._clayMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.9, metalness: 0 });
      this._mats.push(this._clayMat);
      this._root.traverse((o) => { if (o.isMesh && o !== this._window) o.material = this._clayMat; });
    }
  }

  // What is actually in the scene, once, for the console: every mesh and
  // light with its world position and scale. `[metronome] inventory`.
  _logInventory() {
    if (typeof console === 'undefined' || !console.info) return;
    const rows = [];
    const names = new Map([[this._body, 'body'], [this._floor, 'slot floor (scale)'], [this._table, 'table'], [this._wall, 'wall'], [this._pendulum, 'pendulum'], [this._slider, 'slider'], [this._rod, 'rod'], [this._window, 'window light'], [this._gear, 'escapement']]);
    const v = new THREE.Vector3();
    this._root.updateMatrixWorld(true);
    this._root.traverse((o) => {
      if (!(o.isMesh || o === this._pendulum || o === this._slider || o === this._table)) return;
      o.getWorldPosition(v);
      rows.push({ what: names.get(o) || o.type, at: v.toArray().map((x) => +x.toFixed(2)).join(','), scale: o.scale.toArray().map((x) => +x.toFixed(2)).join(','), visible: o.visible });
    });
    for (const l of this._lights) {
      l.getWorldPosition(v);
      rows.push({ what: l.type, at: v.toArray().map((x) => +x.toFixed(2)).join(','), scale: 'intensity ' + (l.intensity != null ? l.intensity : '-'), visible: l.visible });
    }
    rows.push({ what: 'camera', at: this._camera.position.toArray().map((x) => +x.toFixed(2)).join(','), scale: 'aim y ' + this._aimY.toFixed(2) + ', dist ' + this._dist.toFixed(2) + ', plunge ' + (this._plunge * 180 / Math.PI).toFixed(0) + ' deg', visible: true });
    rows.push({ what: 'body', at: 'height ' + BODY_H.toFixed(2) + ', base ' + BASE.toFixed(2), scale: 'slot y ' + SLOT_Y0.toFixed(2) + '..' + SLOT_Y1.toFixed(2) + ', pivot y ' + PIVOT_Y.toFixed(2), visible: true });
    console.info('[metronome] inventory (tier ' + this._tier + ', layer ' + (this._debugLayer || 'all') + ', ambient ' + (this._debugAmbient ? 'forced' : 'normal') + (this._single ? ', single' : '') + ')');
    if (console.table) console.table(rows); else console.info(rows);
  }

  // --- the frame ------------------------------------------------------------------

  update(deltaTime, profile) {
    const dt = Math.min(deltaTime, 0.1);
    this._time += dt;
    this._paused = profile.playbackState === 'PAUSED';
    this._progress = clamp01(profile.progress || 0);

    const director = profile.director || {};
    const live = director.hasReliableGrid && director.bands;
    this._lowBand = live ? clamp01(director.bands.low) : 0.5;

    // --- the tempo. The weight goes where the law says, in SLIDE_S.
    const trackBpm = profile.trackFeatures && profile.trackFeatures.bpm;
    let bpm = this._forcedBpm != null ? this._forcedBpm : (trackBpm || DEFAULT_BPM);
    bpm = Math.max(40, Math.min(208, bpm));
    if (this._forceSlide) { this._forceSlide = false; bpm = bpm > 120 ? 72 : 160; this._forcedBpm = bpm; }
    if (bpm !== this._bpm) {
      this._bpm = bpm;
      this._hFrom = this._h;
      this._hTo = heightForBpm(bpm);
      this._slideT = 0;
    }
    if (this._slideT < 1) {
      this._slideT = Math.min(1, this._slideT + dt / SLIDE_S);
      const u = this._slideT, e = u * u * (3 - 2 * u);
      this._h = this._hFrom + (this._hTo - this._hFrom) * e;
    }
    // the pendulum's own rate, from where the weight IS - during a slide the
    // period drifts with it, which is what a real one does
    const omega = 2 * Math.PI / periodForHeight(this._h);

    // --- the phase. Locked to the beat grid when there is one: phi_target =
    // pi * beat, so the ends of the travel fall on the beats. Pulled, not
    // snapped: after a slide the lock is regained over a few beats.
    const hasGrid = !this._paused && profile.beatIndex != null && profile.beatPhase != null && trackBpm && this._forcedBpm == null;
    if (!this._paused) {
      if (hasGrid) {
        const target = Math.PI * (profile.beatIndex + profile.beatPhase);
        let err = target - this._phi;
        err = err - Math.round(err / (2 * Math.PI)) * 2 * Math.PI;
        this._phi += omega * dt + err * Math.min(1, LOCK_RATE * dt);
      } else {
        this._phi += omega * dt;
      }
      this._amp = Math.min(1, this._amp + dt / RESUME_S);
    } else {
      // PAUSE: the escapement is released and the swing dies to the centre
      // in STOP_S, still ticking through the middle as a real one does
      this._phi += omega * dt;
      this._amp = Math.max(0, this._amp - dt / STOP_S);
    }
    // the click: the phase crossing a multiple of pi, at either end
    const half = Math.floor(this._phi / Math.PI);
    if (this._lastHalf == null) this._lastHalf = half;
    if (half > this._lastHalf) {
      const fire = this._amp > 0.05;
      this._lastHalf = half;
      if (fire) this._click(half % 2 === 0 ? 1 : -1);
    }

    // --- the light of the room: the window's patch, its hue from the palette
    // on the phrase, its warmth from the hour the track is at
    const phrase = profile.phraseIndex;
    if (phrase != null && phrase !== this._lastPhraseIndex) {
      if (this._lastPhraseIndex != null) this._phraseCount++;
      this._lastPhraseIndex = phrase;
      this._pickTint(profile);
    } else if (phrase == null) {
      const p = Math.floor(this._phi / Math.PI / BEATS_PER_PHRASE);
      if (p !== this._synthPhrase) { this._synthPhrase = p; this._phraseCount++; this._pickTint(profile); }
    }
    if (profile.artworkPalette !== this._lastPalette) { this._lastPalette = profile.artworkPalette; this._pickTint(profile); }
    this._tint.lerp(this._tintTarget, Math.min(1, dt * 0.5));
    const warm = this._progress;
    const hour = new THREE.Color(
      LIGHT_MORNING[0] + (LIGHT_EVENING[0] - LIGHT_MORNING[0]) * warm,
      LIGHT_MORNING[1] + (LIGHT_EVENING[1] - LIGHT_MORNING[1]) * warm,
      LIGHT_MORNING[2] + (LIGHT_EVENING[2] - LIGHT_MORNING[2]) * warm);
    hour.multiply(this._tint);
    if (this._window) {
      // the second light of the scene, COLD, on the right: the hour warms it
      // only a little, and it slides across the wall with progress
      const cold = new THREE.Color(LIGHT_MORNING[0], LIGHT_MORNING[1], LIGHT_MORNING[2]).multiply(this._tint);
      this._windowUniforms.uColor.value.copy(cold).lerp(hour, 0.35);
      this._window.position.x = 0.9 + this._progress * 1.3;
      this._window.position.y = 2.6 - this._progress * 0.5;
    }
    this._fill.color.copy(hour).lerp(new THREE.Color(0x8fa8d8), 0.6);

    this._placeCamera(this._time);
    this._placeFrame();
    this._applyPendulum();
  }

  _pickTint(profile) {
    const pal = profile.artworkPalette;
    const vivid = pal && pal.vividColors && pal.vividColors.length ? pal.vividColors : null;
    if (!vivid) { this._tintTarget.set(1, 1, 1); return; }
    const c = new THREE.Color(vivid[this._phraseCount % vivid.length]);
    const hsl = { h: 0, s: 0, l: 0 };
    c.getHSL(hsl);
    c.setHSL(hsl.h, hsl.s * PALETTE_SHARE, 0.62);
    this._tintTarget.copy(c);
  }

  dispose() {
    if (this._rendererRef && this._shadowWasEnabled != null) {
      this._rendererRef.shadowMap.enabled = this._shadowWasEnabled;
    }
    if (this._scene && this._quad) { this._quad.onBeforeRender = null; this._scene.remove(this._quad); }
    if (this._quad) { this._quad.geometry.dispose(); this._quadMat.dispose(); }
    if (this._rt) this._rt.dispose();
    if (this._inner && this._root) this._inner.remove(this._root);
    if (this._inner) for (const l of this._lights || []) this._inner.remove(l);
    for (const g of this._geos || []) g.dispose();
    for (const m of this._mats || []) m.dispose();
    if (this._plateTex) this._plateTex.dispose();
    if (this._woodTex) this._woodTex.dispose();
    if (this._windowMat) this._windowMat.dispose();
    if (this._clayMat) this._clayMat.dispose();
    if (this._key && this._key.shadow && this._key.shadow.map) this._key.shadow.map.dispose();
    this._root = null; this._pendulum = null; this._slider = null; this._gear = null;
    this._inner = null; this._quad = null; this._rt = null;
    this._geos = []; this._mats = [];
  }
}

const _size = new THREE.Vector2();
